import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box,
  Link, Alert, CircularProgress, FormControl, InputLabel,
  Select, MenuItem, Grid, InputAdornment, IconButton,
} from '@mui/material';
import { 
  Visibility, VisibilityOff, CheckCircleOutline 
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { register, clearError, logout } from '../../store/slices/authSlice';
import axios from 'axios';

const SECURITY_QUESTIONS = [
  "What was the name of your first pet?",
  "In what city were you born?",
  "What was your childhood nickname?",
  "What is your mother's maiden name?",
  "What was the name of your first school?",
];

const COLORS = {
  forestGreen: '#228B22',
  charcoal: '#36454F',
  lightGreen: '#32CD32',
  sage: '#9CAF88',
  cream: '#F5F5DC',
};

const validationSchema = yup.object({
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  user_type: yup.string().required('User type is required'),
  security_question: yup.string().required('Security question is required'),
  security_answer: yup.string().required('Security answer is required'),
  password: yup
    .string()
    .min(8, 'Password should be at least 8 characters')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .required('Password is required'),
  confirm_password: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

const RegisterPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error } = useAppSelector((state) => state.auth);
  
  const [showError, setShowError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false); 
  const [registeredEmail, setRegisteredEmail] = useState(''); 
  
  const initRef = useRef(false);

  useEffect(() => {
    const initAuth = async () => {
      if (initRef.current) return;
      initRef.current = true;
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        if (!document.cookie.includes('csrftoken')) {
          await axios.get(`${API_BASE_URL}/auth/csrf-token/`, { withCredentials: true });
        }
      } catch (err) {
        console.error("Auth initialization failed", err);
      }
    };
    initAuth();
    return () => { dispatch(clearError()); };
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      first_name: '', last_name: '', email: '', phone_number: '',
      user_type: '', company_name: '', security_question: '',
      security_answer: '', password: '', confirm_password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setShowError(false);
      const payload = { ...values, password2: values.confirm_password };
      delete payload.confirm_password;

      const resultAction = await dispatch(register(payload));

      if (register.fulfilled.match(resultAction)) {
        setRegisteredEmail(values.email);
        setIsSuccess(true); 
        dispatch(logout()); 
        localStorage.clear();
      } else {
        setShowError(true);
      }
    },
  });

  const handleProceedToLogin = () => {
    navigate('/login', { state: { email: registeredEmail }, replace: true });
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(255,255,255,0.8)',
      '& fieldset': { borderColor: `${COLORS.sage}50` },
      '&:hover fieldset': { borderColor: COLORS.forestGreen },
      '&.Mui-focused fieldset': { borderColor: COLORS.forestGreen, borderWidth: 2 },
    },
    '& .MuiInputLabel-root': { color: COLORS.charcoal, opacity: 0.7 },
    '& .MuiInputLabel-root.Mui-focused': { color: COLORS.forestGreen },
  };

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
        {showPassword ? <Visibility /> : <VisibilityOff />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box sx={{
      minHeight: '100vh', py: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(135deg, ${COLORS.cream} 0%, #e8f5e9 50%, ${COLORS.sage}40 100%)`,
    }}>
      <Container maxWidth="md">
        <Paper elevation={0} sx={{
          p: { xs: 4, md: 6 }, borderRadius: 3, background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)', border: `1px solid ${COLORS.sage}30`,
          boxShadow: `0 25px 50px -12px ${COLORS.charcoal}20`,
        }}>
          
          {isSuccess ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleOutline sx={{ fontSize: 80, color: COLORS.forestGreen, mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, color: COLORS.charcoal, mb: 2 }}>
                Registration Successful
              </Typography>
              <Typography variant="body1" sx={{ color: COLORS.charcoal, opacity: 0.8, mb: 4 }}>
                Your account for <strong>{registeredEmail}</strong> has been created. 
                Please sign in to access your dashboard.
              </Typography>
              <Button 
                fullWidth 
                variant="contained" 
                size="large" 
                onClick={handleProceedToLogin}
                sx={{ 
                  py: 1.5, borderRadius: 2, 
                  background: `linear-gradient(135deg, ${COLORS.forestGreen} 0%, ${COLORS.lightGreen} 100%)`,
                  fontWeight: 700 
                }}
              >
                Proceed to Login
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: COLORS.charcoal }}>Create Account</Typography>
                <Typography variant="body1" sx={{ color: COLORS.charcoal, opacity: 0.7 }}>Join the Rengigs ecosystem</Typography>
              </Box>

              {showError && error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setShowError(false)}>
                  {typeof error === 'object' ? 'Registration failed. Check your details.' : error}
                </Alert>
              )}

              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="first_name" label="First Name" value={formik.values.first_name} onChange={formik.handleChange} error={formik.touched.first_name && !!formik.errors.first_name} helperText={formik.touched.first_name && formik.errors.first_name} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="last_name" label="Last Name" value={formik.values.last_name} onChange={formik.handleChange} error={formik.touched.last_name && !!formik.errors.last_name} helperText={formik.touched.last_name && formik.errors.last_name} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth name="email" label="Email Address" value={formik.values.email} onChange={formik.handleChange} error={formik.touched.email && !!formik.errors.email} helperText={formik.touched.email && formik.errors.email} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={inputSx} error={formik.touched.user_type && !!formik.errors.user_type}>
                      <InputLabel>I am a...</InputLabel>
                      <Select name="user_type" value={formik.values.user_type} label="I am a..." onChange={formik.handleChange}>
                        <MenuItem value="organization">Organization</MenuItem>
                        <MenuItem value="supplier">Supplier/Vendor</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="company_name" label="Company Name" value={formik.values.company_name} onChange={formik.handleChange} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={inputSx} error={formik.touched.security_question && !!formik.errors.security_question}>
                      <InputLabel>Security Question</InputLabel>
                      <Select name="security_question" value={formik.values.security_question} label="Security Question" onChange={formik.handleChange}>
                        {SECURITY_QUESTIONS.map((q) => <MenuItem key={q} value={q}>{q}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth name="security_answer" label="Security Answer" value={formik.values.security_answer} onChange={formik.handleChange} error={formik.touched.security_answer && !!formik.errors.security_answer} helperText={formik.touched.security_answer && formik.errors.security_answer} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      fullWidth 
                      name="password" 
                      label="Password" 
                      type={showPassword ? 'text' : 'password'} 
                      value={formik.values.password} 
                      onChange={formik.handleChange} 
                      error={formik.touched.password && !!formik.errors.password} 
                      helperText={formik.touched.password && formik.errors.password}
                      sx={inputSx}
                      InputProps={{ endAdornment: passwordAdornment }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      fullWidth 
                      name="confirm_password" 
                      label="Confirm Password" 
                      type={showPassword ? 'text' : 'password'} 
                      value={formik.values.confirm_password} 
                      onChange={formik.handleChange} 
                      error={formik.touched.confirm_password && !!formik.errors.confirm_password} 
                      helperText={formik.touched.confirm_password && formik.errors.confirm_password}
                      sx={inputSx} 
                      InputProps={{ endAdornment: passwordAdornment }}
                    />
                  </Grid>
                </Grid>

                <Button type="submit" fullWidth variant="contained" size="large" disabled={isLoading}
                  sx={{ mt: 4, mb: 3, py: 1.5, borderRadius: 2, background: `linear-gradient(135deg, ${COLORS.forestGreen} 0%, ${COLORS.lightGreen} 100%)`, fontWeight: 700 }}>
                  {isLoading ? <CircularProgress size={24} sx={{ color: COLORS.cream }} /> : 'Create Account'}
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2">
                    Already have an account? <Link component={RouterLink} to="/login" sx={{ color: COLORS.forestGreen, fontWeight: 700, textDecoration: 'none' }}>Sign in</Link>
                  </Typography>
                </Box>
              </form>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default RegisterPage;